/**
 * Safe-zone geometry for the vertical (9:16) ad placements the
 * /tools/ad-safe-zone-checker overlay draws.
 *
 * Every number here was read from the platform's own documentation on
 * 2026-10-09 — nothing is eyeballed from a screenshot or copied from a
 * third-party cheat sheet:
 *
 * - Meta: "Consider leaving at least 14% of the top, 35% of the bottom, and 6%
 *   on each side" (Instagram Reels and Instagram Stories ad specs), and "leave
 *   the bottom 40% of your ad free" when a Reels ad carries a disclaimer (the
 *   Business Help Center article on text overlays and the safe zone).
 * - TikTok: measured from the official "In-Feed Standard Version LTR" safe-zone
 *   PNG linked from the TikTok Auction In-Feed Ads help article. The template
 *   is drawn on a 720 × 1280 canvas: 80 px each side, 160 px top, 440 px
 *   bottom, plus a 120 px column inside the right margin from y = 560 down.
 * - YouTube: the "Safe zones for vertical video ads on YouTube" diagram in
 *   Google Ads Help (1080 × 1920): 288 px top, 672 px bottom, 48 px left,
 *   192 px right.
 *
 * Geometry is stored as fractions of the frame (0–1) so it scales to any 9:16
 * export. A safe area is a union of rectangles because TikTok's is not a plain
 * box — the right-hand action column cuts a notch out of its lower half.
 */

export interface FracRect {
  /** Left edge, fraction of frame width. */
  x: number
  /** Top edge, fraction of frame height. */
  y: number
  w: number
  h: number
}

export interface SafeZoneSource {
  label: string
  url: string
}

export interface SafeZoneSpec {
  id: string
  label: string
  /** Short platform name for compact UI. */
  platform: string
  /** The reference canvas the source documents its numbers on. */
  reference: { width: number; height: number }
  /** Union of rectangles that is safe for text, logos and key visuals. */
  safe: FracRect[]
  /** Margins as published, in reference pixels, for the spec table. */
  margins: { top: string; bottom: string; left: string; right: string }
  /** What the source says the covered area is for, or how it varies. */
  note: string
  sources: SafeZoneSource[]
}

export const SAFE_ZONES_VERIFIED_ON = "2026-10-09"

export const META_SAFE_ZONE_HELP_URL =
  "https://www.facebook.com/business/help/980593475366490"
export const META_REELS_SPEC_URL =
  "https://www.facebook.com/business/ads-guide/update/video/instagram-reels"
export const META_STORIES_SPEC_URL =
  "https://www.facebook.com/business/ads-guide/update/image/instagram-story"
export const TIKTOK_IN_FEED_SPEC_URL =
  "https://ads.tiktok.com/help/article/tiktok-auction-in-feed-ads?lang=en"
export const YOUTUBE_VERTICAL_SAFE_ZONE_URL =
  "https://support.google.com/google-ads/answer/9128498?hl=en"

const rect = (x: number, y: number, w: number, h: number): FracRect => ({
  x,
  y,
  w,
  h,
})

/** Margins (fractions) → the single safe rectangle they leave. */
const inset = (top: number, right: number, bottom: number, left: number) =>
  rect(left, top, 1 - left - right, 1 - top - bottom)

export const SAFE_ZONE_SPECS: SafeZoneSpec[] = [
  {
    id: "tiktok-in-feed",
    label: "TikTok In-Feed",
    platform: "TikTok",
    reference: { width: 720, height: 1280 },
    safe: [
      // Main column: x 80–520, y 160–840 (of 720 × 1280).
      rect(80 / 720, 160 / 1280, 440 / 720, 680 / 1280),
      // Upper-right strip above the action column: x 520–640, y 160–560.
      rect(520 / 720, 160 / 1280, 120 / 720, 400 / 1280),
    ],
    margins: {
      top: "160 px (12.5%)",
      bottom: "440 px (34.4%)",
      left: "80 px (11.1%)",
      right: "80 px (11.1%); 200 px (27.8%) below y = 560",
    },
    note: "Standard In-Feed template, vertical, without an anchor. TikTok says the safe zone also depends on ad caption length and any extra formats (anchors have their own template), and that previews can differ slightly from the live ad on a given device.",
    sources: [
      {
        label: "TikTok Ads Manager: TikTok Auction In-Feed Ads (Safe Zone section, In-Feed Standard LTR file)",
        url: TIKTOK_IN_FEED_SPEC_URL,
      },
    ],
  },
  {
    id: "meta-reels",
    label: "Instagram & Facebook Reels",
    platform: "Meta",
    reference: { width: 1080, height: 1920 },
    safe: [inset(0.14, 0.06, 0.35, 0.06)],
    margins: {
      top: "14% (≈269 px)",
      bottom: "35% (672 px)",
      left: "6% (≈65 px)",
      right: "6% (≈65 px)",
    },
    note: "Meta asks for these margins so key elements are not covered by the profile icon or call-to-action, or cropped on screens taller than 9:16, where the ad may be zoomed to fill the screen.",
    sources: [
      { label: "Meta Ads Guide: Instagram Reels ad specs", url: META_REELS_SPEC_URL },
      {
        label: "Meta Business Help Center: text overlays and the safe zone",
        url: META_SAFE_ZONE_HELP_URL,
      },
    ],
  },
  {
    id: "meta-reels-disclaimer",
    label: "Reels with a disclaimer",
    platform: "Meta",
    reference: { width: 1080, height: 1920 },
    safe: [inset(0.14, 0.06, 0.4, 0.06)],
    margins: {
      top: "14% (≈269 px)",
      bottom: "40% (768 px)",
      left: "6% (≈65 px)",
      right: "6% (≈65 px)",
    },
    note: "When a Reels ad includes a disclaimer, Meta says to leave the bottom 40% free of text, logos and other key creative elements instead of 35%.",
    sources: [
      {
        label: "Meta Business Help Center: text overlays and the safe zone",
        url: META_SAFE_ZONE_HELP_URL,
      },
    ],
  },
  {
    id: "meta-stories",
    label: "Instagram & Facebook Stories",
    platform: "Meta",
    reference: { width: 1080, height: 1920 },
    safe: [inset(0.14, 0.06, 0.35, 0.06)],
    margins: {
      top: "14% (≈269 px)",
      bottom: "35% (672 px)",
      left: "6% (≈65 px)",
      right: "6% (≈65 px)",
    },
    note: "Meta applies one safe-zone rule to 9:16 ads in Stories, Reels and Feed. One Meta page — Facebook Stories video specs — still shows an older 14% top / 20% bottom figure; this overlay uses the stricter 14/35/6 from the help article and the Instagram Stories specs.",
    sources: [
      { label: "Meta Ads Guide: Instagram Stories ad specs", url: META_STORIES_SPEC_URL },
      {
        label: "Meta Business Help Center: text overlays and the safe zone",
        url: META_SAFE_ZONE_HELP_URL,
      },
    ],
  },
  {
    id: "youtube-shorts",
    label: "YouTube Shorts & vertical ads",
    platform: "YouTube",
    reference: { width: 1080, height: 1920 },
    safe: [rect(48 / 1080, 288 / 1920, 840 / 1080, 960 / 1920)],
    margins: {
      top: "288 px (15%)",
      bottom: "672 px (35%)",
      left: "48 px (4.4%)",
      right: "192 px (17.8%)",
    },
    note: "Google's diagram covers vertical video ads across YouTube inventory, including Shorts. The wider right margin leaves room for the buttons stacked on that side.",
    sources: [
      {
        label: "Google Ads Help: Safe zones for vertical video ads on YouTube",
        url: YOUTUBE_VERTICAL_SAFE_ZONE_URL,
      },
    ],
  },
]

function intersect(a: FracRect, b: FracRect): FracRect | null {
  const x1 = Math.max(a.x, b.x)
  const y1 = Math.max(a.y, b.y)
  const x2 = Math.min(a.x + a.w, b.x + b.w)
  const y2 = Math.min(a.y + a.h, b.y + b.h)
  if (x2 - x1 <= 1e-6 || y2 - y1 <= 1e-6) return null
  return rect(x1, y1, x2 - x1, y2 - y1)
}

/** Intersection of two unions of rectangles. */
function intersectUnions(a: FracRect[], b: FracRect[]): FracRect[] {
  const out: FracRect[] = []
  for (const ra of a) {
    for (const rb of b) {
      const r = intersect(ra, rb)
      if (r) out.push(r)
    }
  }
  return out
}

/**
 * The area that is safe on every standard placement at once (TikTok In-Feed,
 * Meta Reels/Stories, YouTube Shorts) — what to design to when one 9:16 master
 * runs everywhere. The disclaimer variant is left out: it is a special case,
 * not a placement.
 */
export const COMBINED_SAFE_ZONE: SafeZoneSpec = {
  id: "all-vertical",
  label: "All four at once (strictest)",
  platform: "All",
  reference: { width: 1080, height: 1920 },
  safe: ["tiktok-in-feed", "meta-reels", "meta-stories", "youtube-shorts"]
    .map(id => SAFE_ZONE_SPECS.find(s => s.id === id)!.safe)
    .reduce((acc, next) => intersectUnions(acc, next)),
  margins: {
    top: "15% (YouTube)",
    bottom: "35% (Meta, YouTube)",
    left: "11.1% (TikTok)",
    right: "17.8% (YouTube); 27.8% below 43.75% height (TikTok)",
  },
  note: "The overlap of every placement's safe area — the tightest margin from each side. Content inside it is clear on TikTok In-Feed, Meta Reels and Stories, and YouTube Shorts.",
  sources: [],
}

export const ALL_SAFE_ZONE_OPTIONS: SafeZoneSpec[] = [
  ...SAFE_ZONE_SPECS,
  COMBINED_SAFE_ZONE,
]

/** Share of the frame area left safe, for the readout (0–1). Rects never overlap. */
export function safeAreaShare(spec: SafeZoneSpec): number {
  return spec.safe.reduce((sum, r) => sum + r.w * r.h, 0)
}
